/**
 * Pricing catalog — credit cost per operation, plans, limits and top-ups.
 *
 * Served by the entitlement service from core/backend/pricing_catalog.py, the single
 * source of truth. UI copy must read costs from here instead of restating numbers.
 */
import { useEffect, useState } from 'react';
import { apiClient } from '@/services/api';

export type CreditOperationKey =
  | 'campaign_generation'
  | 'campaign_extra_turn'
  | 'image_generation'
  | 'image_generation_premium'
  | 'image_generation_product_range'
  | 'template_generation_static'
  | 'template_generation_video'
  | 'video_generation'
  | 'text_overlay'
  | 'template_render';

export interface CatalogPlan {
  name: string;
  display_name: string;
  tagline: string;
  monthly_credits: number;
  price: Record<'USD' | 'INR', { monthly: number | null; yearly: number | null; starting_monthly: number | null }>;
  platforms: string[];
  features: Record<string, boolean | string>;
  limits: Record<string, number | boolean | string[]>;
  highlights: string[];
  recommended: boolean;
}

export interface PricingCatalog {
  operations: Record<CreditOperationKey, { credits: number; label: string; note: string }>;
  campaign: { included_turns: number; max_extra_turns: number };
  ad_studio: { composes_per_brief: number; animates_per_brief: number };
  plans: CatalogPlan[];
  top_ups: { credits: number; price: Record<'USD' | 'INR', number> }[];
}

let cached: PricingCatalog | null = null;
let inflight: Promise<PricingCatalog> | null = null;

export const loadPricingCatalog = (): Promise<PricingCatalog> => {
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = apiClient
      .get<PricingCatalog>('/api/v1/plans/catalog')
      .then((res) => (cached = res.data))
      .finally(() => {
        inflight = null;
      });
  }
  return inflight;
};

/** The catalog, or null while it loads (render cost copy only once it's present). */
export function usePricingCatalog(): PricingCatalog | null {
  const [catalog, setCatalog] = useState<PricingCatalog | null>(cached);
  useEffect(() => {
    if (catalog) return;
    let active = true;
    loadPricingCatalog()
      .then((c) => active && setCatalog(c))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [catalog]);
  return catalog;
}

/**
 * Fill `{credits:<operation>}`, `{campaign:<key>}` and `{ad_studio:<key>}` tokens in static
 * copy from the catalog, so help text never restates a number.
 */
export function formatCatalogText(text: string, catalog: PricingCatalog | null): string {
  return text.replace(/\{(credits|campaign|ad_studio):([a-z_]+)\}/g, (_, group: string, key: string) => {
    if (!catalog) return '…';
    const value =
      group === 'credits'
        ? catalog.operations[key as CreditOperationKey]?.credits
        : (catalog[group as 'campaign' | 'ad_studio'] as Record<string, number>)[key];
    return value === undefined ? '…' : String(value);
  });
}

/** Credits for one operation, or undefined while the catalog loads. */
export function useCreditCost(op: CreditOperationKey): number | undefined {
  return usePricingCatalog()?.operations[op]?.credits;
}

/** Ad Studio cost of one creative (fresh or another size) of the given type. */
export function useAdStudioCost(type: 'static' | 'video'): number | undefined {
  return useCreditCost(type === 'video' ? 'template_generation_video' : 'template_generation_static');
}
